/*
 * What's Wrong? — the episodes, as data. An episode is these fields and
 * nothing else; cards.js turns them into cards, so adding episode 06 means
 * adding one object here, never touching the look.
 *
 * Read by the student app's /whatswrong page and by the Instagram renderer
 * (sciencexp-app/scripts/insta/whats-wrong), so both always show the same words.
 *
 * Figures are SVG fragments drawn with series.css classes. Animation hooks,
 * all read by series.js on Card A only:
 *   data-t="a b"             fade in between a and b seconds
 *   data-arrow="x1 y1 x2 y2" an arrow that grows from x1,y1 to x2,y2 over data-t
 *   data-from="x1 y1 x2 y2"  …or morphs from these coordinates instead of growing
 *   data-move="dx dy"        starts offset by dx,dy and eases to rest (decelerates)
 *   data-spin="deg cx cy"    turns by deg about (cx, cy) over data-t
 *   data-ease="in"           on a data-move, data-from or data-spin: accelerate instead
 *   data-ease="linear"       …or keep a steady pace (uniform motion)
 *   data-loop="s"            on a data-spin: deg every s seconds from the start of
 *                            data-t, never stopping — it keeps turning through the hold
 * Card B's figure animates the same way where it is played (the reel); a still
 * shows its last frame.
 *   data-w, data-both        arrow stroke width; a head at both ends
 *
 * `practice: { slug }` — the page's last step, a published practice link to a
 * public question set of 1–3 bank questions on the same idea (campaign
 * `whats-wrong`). Leave it out until the link is live; the step is then hidden.
 * Card A animates for cards.js's DURATION seconds, then holds its last frame.
 */
// A post's poll is an Instagram caption add-on: 2–4 options of at most 25
// characters each. Where an episode has one, the page's commit question is the
// same poll, letter for letter, so the post and the page ask the same thing.
const POLL_07 = {
  question: 'Friction on each wheel points…',
  options: ['Both forward', 'Both backward', 'Front: back · Rear: fwd', 'Front: fwd · Rear: back'],
  answer: 2,
};

export const EPISODES = [
  {
    id: '01',
    slug: 'normal-force',
    title: 'Normal force and weight',
    topic: "Newton's laws of motion",
    sim: 'newton-second-law',
    setup: 'A person stands still on the ground.',
    figA: { box: '0 0 600 330', svg: `
      <g data-t="0.3 0.9">
        <rect x="30" y="262" width="270" height="20" class="ground-hatch"/>
        <line x1="30" y1="262" x2="300" y2="262" class="ln"/>
        <circle cx="165" cy="92" r="22" class="solid"/>
        <g class="person">
          <line x1="165" y1="122" x2="165" y2="190"/>
          <line x1="165" y1="140" x2="135" y2="182"/><line x1="165" y1="140" x2="195" y2="182"/>
          <line x1="165" y1="190" x2="148" y2="256"/><line x1="165" y1="190" x2="182" y2="256"/>
        </g>
      </g>
      <g data-t="0.7 1.1">
        <rect x="345" y="18" width="235" height="300" rx="12" class="ln-dash"/>
        <text x="462" y="50" text-anchor="middle" font-size="19" class="t-mute">forces on the person</text>
        <circle cx="462" cy="172" r="8" class="fill-ink"/>
      </g>
      <g data-arrow="462 172 462 72" data-w="6" data-t="1.1 1.8" class="a-blue"></g>
      <text x="482" y="98" font-size="38" class="t-v t-blue" data-t="1.6 1.9">N</text>
      <g data-arrow="462 172 462 290" data-w="6" data-t="1.9 2.6" class="a-ink"></g>
      <text x="482" y="286" font-size="36" class="t-v" data-t="2.4 2.7">mg</text>` },
    claim: '<span class="v">N</span> and <span class="v">mg</span> are equal and opposite. By Newton\'s third law, <span class="v">N</span> = <span class="v">mg</span>.',
    b: {
      said: '<span class="v">N</span> and <span class="v">mg</span> are equal and opposite. <s>By Newton\'s third law</s>, <span class="v">N</span> = <span class="v">mg</span>.',
      pen: 'Both forces act on the person. A third-law pair acts on two different bodies.',
      fix: `<p><b><span class="v">N</span> = <span class="v">mg</span></b> because the person isn't accelerating: <span class="v">N</span> − <span class="v">mg</span> = 0, Newton's <b>second</b> law.</p>
            <p class="k2">The real third-law pairs</p>
            <ol class="ww-pairs">
              <li><span><b>Earth ↔ person</b> pull each other</span><i class="v">mg</i></li>
              <li><span><b>Person ↔ ground</b> push each other</span><i class="v">N</i></li>
            </ol>`,
    },
    commit: {
      prompt: 'Which step is wrong?',
      options: [
        '<span class="v">N</span> and <span class="v">mg</span> are equal and opposite.',
        'By Newton\'s third law,',
        '<span class="v">N</span> = <span class="v">mg</span>.',
      ],
      answer: 1,
    },
    check: {
      question: 'A book rests on a table. The table pushes the book up. What is the third-law partner of that push?',
      options: ['The book\'s weight', 'The book pushing down on the table', 'The table\'s weight', 'There isn\'t one: the book is at rest'],
      answer: 1,
      why: 'Third-law partners act on different bodies and are the same kind of force. Table pushes book ↔ book pushes table.',
    },
    practice: { slug: 'ww-01' },
    caption: `What's wrong? #01
A person stands still: N = mg, by Newton's third law.
The answer is right. Something else isn't.
Comment what's wrong, then swipe to check.
#physics #newtonslaws #whatswrong`,
    adText: "The answer is right. The reason isn't. Can you spot what's wrong? Swipe to check, then try it yourself: free, in your browser, no account.",
    // The same ask for a reel ad: no carousel, so nothing to swipe.
    reelAdText: 'The answer is right. The reason isn\'t. Can you spot what\'s wrong before the answer appears? Then try it yourself: free, in your browser, no account.',
  },

  {
    id: '02',
    slug: 'sign-of-acceleration',
    title: 'Sign of acceleration',
    topic: 'Kinematics',
    sim: 'motion-graphs',
    setup: 'A cart moves left and is slowing down. Right is positive.',
    figA: { box: '0 0 600 300', svg: `
      <g data-arrow="30 262 572 262" data-w="3" class="a-mute"></g>
      <g class="ln-mute"><line x1="80" y1="254" x2="80" y2="270"/><line x1="200" y1="254" x2="200" y2="270"/><line x1="320" y1="254" x2="320" y2="270"/><line x1="440" y1="254" x2="440" y2="270"/></g>
      <text x="572" y="298" font-size="26" text-anchor="end" class="t-mute">+<tspan class="t-v">x</tspan></text>
      <rect x="470" y="190" width="90" height="44" rx="8" class="ghost" data-t="0.3 0.45"/>
      <rect x="265" y="190" width="90" height="44" rx="8" class="ghost" data-t="0.76 0.91"/>
      <rect x="146" y="190" width="90" height="44" rx="8" class="ghost" data-t="1.22 1.37"/>
      <g data-move="420 0" data-t="0.3 2.6">
        <rect x="50" y="190" width="90" height="44" rx="8" class="solid"/>
        <circle cx="72" cy="242" r="11" class="fill-ink"/><circle cx="118" cy="242" r="11" class="fill-ink"/>
        <text x="106" y="172" font-size="34" class="t-v t-blue">v</text>
      </g>
      <g data-arrow="95 160 15 160" data-from="515 160 295 160" data-w="6" data-t="0.3 2.6" class="a-blue"></g>` },
    claim: 'It is slowing down, so acceleration is negative: <span class="v">a</span> &lt; 0.',
    b: {
      said: 'It is slowing down, <s>so acceleration is negative: <span class="v">a</span> &lt; 0</s>.',
      pen: 'Slowing down means accl<sup>n</sup> points opposite to <span class="v">v</span>, and <span class="v">v</span> points left.',
      fig: { box: '0 0 600 300', svg: `
        <text x="0" y="28" font-size="22" class="t-mute">Slowing down</text>
        <g data-arrow="470 22 572 22" data-w="6" class="a-green"></g>
        <text x="458" y="30" font-size="24" text-anchor="end" class="t-green">accl<tspan font-size="16" dy="-9">n</tspan><tspan dy="9"> &gt; 0</tspan></text>
        <g data-arrow="10 112 592 112" data-w="2.5" class="a-mute"></g>
        <text x="592" y="136" font-size="20" text-anchor="end" class="t-mute">+<tspan class="t-v">x</tspan></text>
        <rect x="440" y="66" width="64" height="34" rx="6" class="ghost" data-t="0.3 0.4"/>
        <rect x="245" y="66" width="64" height="34" rx="6" class="ghost" data-t="0.8 0.9"/>
        <rect x="126" y="66" width="64" height="34" rx="6" class="ghost" data-t="1.3 1.4"/>
        <g data-move="400 0" data-t="0.3 2.8">
          <rect x="40" y="66" width="64" height="34" rx="6" class="solid"/>
          <circle cx="56" cy="103" r="7" class="fill-ink"/><circle cx="88" cy="103" r="7" class="fill-ink"/>
          <text x="80" y="60" font-size="24" class="t-v t-blue">v</text>
        </g>
        <g data-arrow="72 52 52 52" data-from="472 52 362 52" data-w="5" data-t="0.3 2.8" class="a-blue"></g>

        <text x="0" y="186" font-size="22" class="t-mute">Speeding up</text>
        <g data-arrow="572 180 470 180" data-w="6" class="a-red"></g>
        <text x="458" y="188" font-size="24" text-anchor="end" class="t-red">accl<tspan font-size="16" dy="-9">n</tspan><tspan dy="9"> &lt; 0</tspan></text>
        <g data-arrow="10 272 592 272" data-w="2.5" class="a-mute"></g>
        <text x="592" y="296" font-size="20" text-anchor="end" class="t-mute">+<tspan class="t-v">x</tspan></text>
        <rect x="440" y="226" width="64" height="34" rx="6" class="ghost" data-t="0.3 0.4"/>
        <rect x="354" y="226" width="64" height="34" rx="6" class="ghost" data-t="1.8 1.9"/>
        <rect x="235" y="226" width="64" height="34" rx="6" class="ghost" data-t="2.3 2.4"/>
        <g data-move="400 0" data-ease="in" data-t="0.3 2.8">
          <rect x="40" y="226" width="64" height="34" rx="6" class="solid"/>
          <circle cx="56" cy="263" r="7" class="fill-ink"/><circle cx="88" cy="263" r="7" class="fill-ink"/>
          <text x="80" y="220" font-size="24" class="t-v t-blue">v</text>
        </g>
        <g data-arrow="72 212 10 212" data-from="472 212 452 212" data-ease="in" data-w="5" data-t="0.3 2.8" class="a-blue"></g>` },
      fix: `<p>Moving left and slowing down: <b>accl<sup>n</sup> points right, so it is positive.</b> It points left only if the cart speeds up.</p>`,
    },
    commit: {
      prompt: "What's wrong?",
      options: [
        'Right is positive, so the cart\'s velocity is positive',
        '<span class="v">a</span> should be zero, because the cart is slowing steadily',
        'Slowing down means <span class="v">a</span> points opposite to <span class="v">v</span>, so right',
        'Nothing: <span class="v">a</span> &lt; 0 is right',
      ],
      answer: 2,
    },
    check: {
      question: 'A lift is moving down and slowing to a stop. Take up as positive. Which way does its acceleration point?',
      options: ['Down', 'Nowhere: a slowing lift has zero acceleration', 'You can\'t tell', 'Up'],
      answer: 3,
      why: 'Its velocity points down and is shrinking, so the change in velocity points up. <span class="v">a</span> is up, positive.',
    },
    practice: { slug: 'ww-02' },
    caption: `What's wrong? #02
Slowing down, so acceleration is negative… right?
Comment what's wrong, then swipe to check.
#physics #kinematics #whatswrong`,
    adText: 'Slowing down, so negative acceleration? One line of this is wrong. Spot it, swipe to check, then try it yourself: free, in your browser, no account.',
    // The same ask for a reel ad: no carousel, so nothing to swipe.
    reelAdText: 'Slowing down, so negative acceleration? One line of this is wrong. Spot it before the answer appears, then try it yourself: free, in your browser, no account.',
  },

  {
    id: '03',
    slug: 'rolling-spheres',
    title: 'Rolling spheres',
    topic: 'Moment of inertia',
    sim: 'rolling-race',
    setup: 'A solid and a hollow sphere, same mass and radius, roll from the same height.',
    figA: { box: '0 0 600 300', svg: `
      <g data-t="0.3 0.8">
        <path d="M50,260 L280,260 L50,110 Z" class="solid" stroke-linejoin="round"/>
        <path d="M350,260 L580,260 L350,110 Z" class="solid" stroke-linejoin="round"/>
      </g>
      <circle cx="108" cy="119" r="24" class="fill-ink" data-t="0.9 1.3"/>
      <circle cx="408" cy="119" r="21" class="hollow" data-t="1.1 1.5"/>
      <g data-arrow="30 112 30 258" data-w="2.5" data-both="1" data-t="1.6 2.2" class="a-mute"></g>
      <g data-arrow="330 112 330 258" data-w="2.5" data-both="1" data-t="1.6 2.2" class="a-mute"></g>
      <g data-t="2.1 2.5">
        <text x="12" y="196" font-size="28" text-anchor="middle" class="t-v">h</text>
        <text x="312" y="196" font-size="28" text-anchor="middle" class="t-v">h</text>
      </g>
      <g data-t="1.3 1.7">
        <text x="165" y="294" font-size="24" text-anchor="middle">solid</text>
        <text x="465" y="294" font-size="24" text-anchor="middle">hollow</text>
      </g>` },
    claim: 'Same height, same <span class="v">mgh</span>, so the same speed at the bottom. They reach it together.',
    b: {
      said: 'Same height, same <span class="v">mgh</span>, <s>so the same speed at the bottom. They reach it together</s>.',
      // The race, played in the reel and the answer videos: both start together;
      // the solid one is at the bottom while the hollow one is 84% of the way.
      fig: { box: '0 96 600 276', svg: `
        <path d="M50,260 L280,260 L50,110 Z" class="solid" stroke-linejoin="round"/>
        <path d="M350,260 L580,260 L350,110 Z" class="solid" stroke-linejoin="round"/>
        <g data-move="-205 -133" data-ease="in" data-t="0.3 2.3"><circle cx="276" cy="233" r="22" class="fill-ink"/></g>
        <g data-move="-205 -133" data-ease="in" data-t="0.3 2.5"><circle cx="576" cy="233" r="19" class="hollow"/></g>
        <text x="165" y="294" font-size="24" text-anchor="middle">solid</text>
        <text x="465" y="294" font-size="24" text-anchor="middle">hollow</text>
        <text x="200" y="196" font-size="26" class="t-green" data-t="2.3 2.6">first</text>
        <!-- Where mgh went at the bottom: linear KE (moving) and rotational KE
             (spinning). Solid 5/7 : 2/7, hollow 3/5 : 2/5. -->
        <g data-t="2.4 2.8">
          <rect x="50" y="308" width="164" height="28" style="fill: var(--ww-blue)"/>
          <rect x="214" y="308" width="66" height="28" style="fill: #ff8a5c"/>
          <text x="132" y="328" font-size="18" text-anchor="middle" class="fill-ground">71%</text>
          <text x="247" y="328" font-size="18" text-anchor="middle" class="fill-ground">29%</text>
          <rect x="350" y="308" width="138" height="28" style="fill: var(--ww-blue)"/>
          <rect x="488" y="308" width="92" height="28" style="fill: #ff8a5c"/>
          <text x="419" y="328" font-size="18" text-anchor="middle" class="fill-ground">60%</text>
          <text x="534" y="328" font-size="18" text-anchor="middle" class="fill-ground">40%</text>
          <rect x="120" y="352" width="16" height="16" style="fill: var(--ww-blue)"/>
          <text x="144" y="366" font-size="19" class="t-mute">linear KE</text>
          <rect x="330" y="352" width="16" height="16" style="fill: #ff8a5c"/>
          <text x="354" y="366" font-size="19" class="t-mute">rotational KE</text>
        </g>` },
      pen: 'They also spin. The hollow one puts more of mgh into spinning.',
      fix: `<p>Solid: <span class="v">v</span> = √<span class="rt">10<span class="v">gh</span>/7</span></p>
            <p>Hollow: <span class="v">v</span> = √<span class="rt">6<span class="v">gh</span>/5</span>, <b>slower</b>. Its mass is at the rim.</p>`,
    },
    commit: {
      prompt: 'Why is it wrong?',
      options: [
        'Friction slows the hollow sphere more',
        'Part of <span class="v">mgh</span> goes into spinning, and the hollow one spends more on it',
        'The solid one is heavier, so it falls faster',
        'It isn\'t wrong: they tie',
      ],
      answer: 1,
    },
    check: {
      question: 'A solid cylinder and a ring, same mass and radius, roll down the same slope from rest. Which reaches the bottom first?',
      options: ['The cylinder', 'The ring', 'They tie', 'Whichever is heavier'],
      answer: 0,
      why: 'The ring has all its mass at the rim (<span class="v">I</span> = <span class="v">mr</span>², against ½<span class="v">mr</span>²), so more of its energy goes into spin and less into speed.',
    },
    practice: { slug: 'ww-03' },
    caption: `What's wrong? #03
Same mass, same radius, same height. So it's a tie?
Comment what's wrong, then swipe to check.
#physics #rotation #whatswrong`,
    adText: 'Same mass, same radius, same height. So they tie? Spot the mistake, swipe to check, then try it yourself: free, in your browser, no account.',
    // The same ask for a reel ad: no carousel, so nothing to swipe.
    reelAdText: 'Same mass, same radius, same height. So they tie? Call the winner before the race, then try it yourself: free, in your browser, no account.',
  },

  {
    id: '04',
    slug: 'lift-tension',
    title: 'Tension in a lift',
    topic: "Newton's laws of motion",
    sim: 'newton-second-law',
    setup: 'A lift of mass <span class="v">m</span> hangs from a cable and accelerates upward at <span class="v">a</span>.',
    figA: { box: '0 0 600 350', svg: `
      <g data-t="0.3 0.9">
        <line x1="200" y1="0" x2="200" y2="350" class="ln-mute"/><line x1="400" y1="0" x2="400" y2="350" class="ln-mute"/>
        <rect x="190" y="0" width="220" height="12" class="fill-mute"/>
        <circle cx="300" cy="26" r="14" class="solid"/>
        <line x1="300" y1="40" x2="300" y2="120" class="ln" stroke-width="3"/>
        <rect x="230" y="120" width="140" height="150" rx="6" class="solid"/>
        <text x="300" y="208" font-size="38" text-anchor="middle" class="t-v">m</text>
      </g>
      <g data-arrow="455 250 455 140" data-w="6" data-t="0.9 1.4" class="a-green"></g>
      <text x="472" y="200" font-size="34" class="t-v t-green" data-t="1.3 1.6">a</text>
      <g data-arrow="300 120 300 46" data-w="6" data-t="1.5 2.0" class="a-blue"></g>
      <text x="318" y="84" font-size="34" class="t-v t-blue" data-t="1.9 2.2">T</text>
      <g data-arrow="300 270 300 344" data-w="6" data-t="2.0 2.5" class="a-ink"></g>
      <text x="318" y="330" font-size="32" class="t-v" data-t="2.4 2.7">mg</text>` },
    claim: 'It accelerates up at <span class="v">a</span>, but <span class="v">g</span> points down. So they subtract: <span class="eq"><span class="v">T</span> = <span class="v">mg</span> − <span class="v">ma</span></span>.',
    b: {
      said: 'It accelerates up at <span class="v">a</span>, but <span class="v">g</span> points down. So <s>they subtract: <span class="v">T</span> = <span class="v">mg</span> − <span class="v">ma</span></s>.',
      // The forces on the lift, drawn in turn: the cable's pull is the longer
      // arrow, so what is left over — the net force — points up, with a.
      fig: { box: '0 0 600 250', svg: `
        <line x1="300" y1="0" x2="300" y2="70" class="ln" stroke-width="3"/>
        <rect x="245" y="70" width="110" height="110" rx="6" class="solid"/>
        <text x="300" y="134" font-size="30" text-anchor="middle" class="t-v">m</text>
        <g data-arrow="230 125 230 5" data-w="6" data-t="0.3 1.0" class="a-blue"></g>
        <text x="216" y="30" font-size="24" text-anchor="end" class="t-blue" data-t="0.8 1.1">cable <tspan class="t-v">T</tspan></text>
        <g data-arrow="370 125 370 225" data-w="6" data-t="1.0 1.6" class="a-ink"></g>
        <text x="386" y="220" font-size="24" data-t="1.4 1.7">weight <tspan class="t-v">mg</tspan></text>
        <g data-arrow="480 160 480 60" data-w="7" data-t="1.9 2.5" class="a-green"></g>
        <text x="500" y="100" font-size="24" class="t-green" data-t="2.3 2.6">net</text>
        <text x="500" y="128" font-size="24" class="t-green" data-t="2.3 2.6">force</text>` },
      pen: 'g here is not an acceleration to subtract. The force mg is subtracted from T. Do not mix acceleration and force.',
      fix: `<p>Net force = mass × acceleration</p>
            <p class="big"><span class="v">T</span> − <span class="v">mg</span> = <span class="v">ma</span></p>`,
      keep: 'The lift rope needs more than <span class="v">mg</span> of force to pull the lift upwards.',
    },
    commit: {
      prompt: 'Which step is wrong?',
      options: [
        'It accelerates up at <span class="v">a</span>.',
        '<span class="v">g</span> points down.',
        'So they subtract: <span class="v">T</span> = <span class="v">mg</span> − <span class="v">ma</span>.',
      ],
      answer: 2,
    },
    check: {
      question: 'Now the lift is moving up but slowing down at <span class="v">a</span>. What is the tension?',
      options: ['<span class="v">T</span> = <span class="v">m</span>(<span class="v">g</span> + <span class="v">a</span>)', '<span class="v">T</span> = <span class="v">m</span>(<span class="v">g</span> − <span class="v">a</span>)', '<span class="v">T</span> = <span class="v">mg</span>', '<span class="v">T</span> = 0'],
      answer: 1,
      why: 'Slowing while going up means <span class="v">a</span> points down: <span class="v">mg</span> − <span class="v">T</span> = <span class="v">ma</span>, so <span class="v">T</span> = <span class="v">m</span>(<span class="v">g</span> − <span class="v">a</span>), less than <span class="v">mg</span>.',
    },
    practice: { slug: 'ww-04' },
    caption: `What's wrong? #04
Lift accelerating up, gravity pulling down, so T = mg − ma?
Comment what's wrong, then swipe to check.
#physics #newtonslaws #whatswrong`,
    adText: 'A lift accelerates up. g points down, so T = mg − ma? One step is wrong. Spot it, swipe to check, then try it yourself: free, in your browser, no account.',
    // The same ask for a reel ad: no carousel, so nothing to swipe.
    reelAdText: 'A lift accelerates up. g points down, so T = mg − ma? One step is wrong. Spot it before the answer appears, then try it yourself: free, in your browser, no account.',
  },

  {
    id: '05',
    slug: 'equal-opposite-forces',
    title: 'Equal and opposite forces',
    topic: 'Torque',
    sim: 'torque-equilibrium',
    setup: 'Two forces of equal size act on a rigid body in opposite directions.',
    figA: { box: '0 0 600 280', svg: `
      <g data-t="0.3 0.8">
        <path d="M70,150 C60,85 135,55 200,62 C275,70 320,105 312,165 C305,228 232,258 160,248 C100,240 78,200 70,150 Z" class="solid"/>
        <text x="190" y="162" font-size="22" text-anchor="middle" class="t-mute">rigid body</text>
      </g>
      <g data-t="0.8 1.2">
        <rect x="360" y="40" width="220" height="200" rx="12" class="ln-dash"/>
        <text x="470" y="74" font-size="19" text-anchor="middle" class="t-mute">the two forces</text>
      </g>
      <g data-arrow="400 125 530 125" data-w="6" data-t="1.2 1.8" class="a-blue"></g>
      <text x="542" y="136" font-size="32" class="t-v t-blue" data-t="1.7 2.0">F</text>
      <g data-arrow="530 195 400 195" data-w="6" data-t="1.8 2.4" class="a-blue"></g>
      <text x="374" y="206" font-size="32" class="t-v t-blue" data-t="2.3 2.6">F</text>` },
    claim: 'Equal and opposite forces form a couple. So they turn the body.',
    b: {
      said: '<s>Equal and opposite forces form a couple. So they turn the body</s>.',
      pen: 'Only if they act along different lines. Where they act was never given.',
      fig: { box: '0 0 600 250', svg: `
        <g>
          <path d="M60,125 C55,80 110,58 160,62 C215,66 245,95 240,135 C235,180 185,200 135,193 C92,187 64,160 60,125 Z" class="solid"/>
          <line x1="12" y1="128" x2="288" y2="128" class="ln-dash"/>
          <g data-arrow="70 128 12 128" data-w="5" data-t="0.3 0.9" class="a-blue"></g>
          <g data-arrow="230 128 288 128" data-w="5" data-t="0.3 0.9" class="a-blue"></g>
          <text x="150" y="238" font-size="25" text-anchor="middle">same line: no turn</text>
        </g>
        <g transform="translate(310 0)">
          <!-- Lines apart: the pair turns the body clockwise — the top force pushes right,
               the bottom one left (it spins, faster and faster). -->
          <g data-spin="360 150 128" data-ease="in" data-t="1.1 3.2">
            <path d="M60,125 C55,80 110,58 160,62 C215,66 245,95 240,135 C235,180 185,200 135,193 C92,187 64,160 60,125 Z" class="solid"/>
            <g data-arrow="120 85 280 85" data-w="5" data-t="0.3 0.9" class="a-blue"></g>
            <g data-arrow="180 172 20 172" data-w="5" data-t="0.3 0.9" class="a-blue"></g>
            <g data-arrow="150 93 150 164" data-w="2.5" data-both="1" data-t="0.6 1.0" class="a-yellow"></g>
            <text x="160" y="138" font-size="26" class="t-v t-yellow">d</text>
          </g>
          <text x="150" y="238" font-size="25" text-anchor="middle">apart: it turns</text>
        </g>` },
      fix: `<p class="big">Not enough information.</p>`,
      fixLabel: 'Correct answer',
    },
    commit: {
      prompt: "What's wrong?",
      options: [
        'Equal and opposite forces always cancel, so there is never a torque',
        'A couple needs three forces, not two',
        'We don\'t know where the forces act, and torque depends on that',
        'Nothing: the body turns',
      ],
      answer: 2,
    },
    check: {
      question: 'Two equal forces pull the ends of a rod outward, along the rod\'s own line. The rod was at rest. What happens?',
      options: ['It spins', 'It moves sideways', 'It spins and moves', 'It stays at rest, only stretched'],
      answer: 3,
      why: 'Along one line there is no net force and no torque. The forces only stretch the rod.',
    },
    practice: { slug: 'ww-05' },
    caption: `What's wrong? #05
Equal and opposite forces, so the body turns. Most people agree.
Comment what's wrong, then swipe to check.
#physics #torque #whatswrong`,
    adText: 'Equal and opposite forces, so the body turns? Most people say yes. Spot what\'s wrong, swipe to check: free, in your browser, no account.',
    // The same ask for a reel ad: no carousel, so nothing to swipe.
    reelAdText: 'Equal and opposite forces, so the body turns? Most people say yes. Spot what\'s wrong before the answer appears, then try it yourself: free, in your browser, no account.',
  },

  {
    id: '06',
    slug: 'uniform-circular-motion',
    title: 'Uniform circular motion',
    topic: 'Circular motion',
    sim: 'banking-curve',
    setup: 'On a frictionless table, a ball on a string circles a peg at constant speed <span class="v">v</span>.',
    // Top view: the ball goes round at a steady pace, one turn every 2.2 s, and
    // keeps going through the hold; then the free-body diagram the claim is built on.
    figA: { box: '0 18 600 256', svg: `
      <g data-t="0.3 0.8">
        <circle cx="150" cy="124" r="92" class="ln-dash"/>
        <text x="150" y="266" font-size="28" text-anchor="middle" class="t-mute">from above</text>
      </g>
      <g data-spin="360 150 124" data-loop="2.2" data-t="0.3 2.5">
        <line x1="150" y1="124" x2="242" y2="124" class="ln" stroke-width="3"/>
        <circle cx="242" cy="124" r="17" class="fill-ink"/>
      </g>
      <circle cx="150" cy="124" r="8" class="solid" data-t="0.3 0.8"/>
      <g data-t="0.7 1.1">
        <rect x="312" y="22" width="282" height="248" rx="12" class="ln-dash"/>
        <text x="453" y="62" text-anchor="middle" font-size="27" class="t-mute">forces on the ball</text>
        <circle cx="453" cy="160" r="8" class="fill-ink"/>
      </g>
      <g data-arrow="453 160 334 160" data-w="7" data-t="1.4 2.0" class="a-blue"></g>
      <text x="374" y="138" font-size="46" class="t-v t-blue" data-t="1.8 2.1">T</text>
      <g data-arrow="453 160 572 160" data-w="7" data-t="2.0 2.6" class="a-blue"></g>
      <text x="512" y="138" font-size="38" text-anchor="middle" class="t-v t-blue" data-t="2.4 2.7">mv²/r</text>
      <text x="500" y="214" font-size="30" text-anchor="middle" class="t-blue" data-t="2.4 2.7">centripetal</text>` },
    claim: 'Constant speed <span class="v">v</span> ⇒ zero accl<sup>n</sup> ⇒ <span class="v">T</span> balances the centripetal force.',
    b: {
      said: 'Constant speed <span class="v">v</span> <s>⇒ zero accl<sup>n</sup> ⇒ <span class="v">T</span> balances the centripetal force.</s>',
      pen: 'Its direction keeps changing, so it accelerates inward. <span class="v">T</span> is the centripetal force.',
      // Left: no net force, the ball leaves along the tangent. Right: T alone,
      // always across v, turns it round at the same speed.
      fig: { box: '0 26 600 206', svg: `
        <g>
          <circle cx="150" cy="120" r="78" class="ln-dash"/>
          <line x1="150" y1="42" x2="296" y2="42" class="ln-dash"/>
          <circle cx="150" cy="120" r="6" class="solid"/>
          <g data-move="-130 0" data-ease="linear" data-t="0.3 2.4">
            <circle cx="280" cy="42" r="13" class="fill-ink"/>
          </g>
          <text x="150" y="228" font-size="25" text-anchor="middle">net force 0: straight on</text>
        </g>
        <g transform="translate(310 0)">
          <circle cx="150" cy="120" r="78" class="ln-dash"/>
          <g data-spin="360 150 120" data-ease="linear" data-t="0.3 3.3">
            <line x1="150" y1="120" x2="150" y2="42" class="ln" stroke-width="2.5"/>
            <circle cx="150" cy="42" r="13" class="fill-ink"/>
            <g data-arrow="150 56 150 104" data-w="5" class="a-blue"></g>
            <g data-arrow="164 42 230 42" data-w="5" class="a-blue"></g>
            <text x="136" y="90" font-size="24" text-anchor="end" class="t-v t-blue">T</text>
            <text x="212" y="30" font-size="24" class="t-v t-blue">v</text>
          </g>
          <circle cx="150" cy="120" r="6" class="solid"/>
          <text x="150" y="228" font-size="25" text-anchor="middle">only <tspan class="t-v t-blue">T</tspan>, inward: it turns</text>
        </g>` },
      fix: `<p><b>Inertial frame</b> (fixed to the table):<br><span class="eq"><span class="v">T</span> = <span class="v">mv</span>²/<span class="v">r</span></span>: acceleration toward the centre</p>
            <p><b>Non-inertial frame</b> (rotating with the ball):<br><span class="eq"><span class="v">T</span> − <span class="v">mv</span>²/<span class="v">r</span></span> (centrifugal) = 0: <span class="v">v</span> = 0, <span class="v">a</span> = 0</p>`,
    },
    commit: {
      prompt: "What's wrong?",
      options: [
        'The speed isn\'t really constant',
        '<span class="v">T</span> is bigger than the centripetal force, so the ball moves inward',
        'Constant speed isn\'t zero acceleration: the direction keeps changing, so it accelerates toward the centre',
        'Nothing: the forces balance',
      ],
      answer: 2,
    },
    check: {
      question: 'A car goes round a flat, circular track at a steady 40 km/h. Which way does the net force on it point?',
      options: ['Nowhere: it is zero, the speed is steady', 'Outward, away from the centre', 'Toward the centre of the track', 'Forward, along its motion'],
      answer: 2,
      why: 'Its direction keeps changing, so it is accelerating toward the centre. Friction from the road supplies that force, and nothing balances it.',
    },
    practice: { slug: 'ww-06' },
    caption: `What's wrong? #06
Constant speed, so zero acceleration… right?
Comment what's wrong, then swipe to check.
#physics #circularmotion #whatswrong`,
    adText: 'Constant speed, so zero acceleration? One step is wrong. Spot it, swipe to check, then try it yourself: free, in your browser, no account.',
    // The same ask for a reel ad: no carousel, so nothing to swipe.
    reelAdText: 'Constant speed, so zero acceleration? One step is wrong. Spot it before the answer appears, then try it yourself: free, in your browser, no account.',
  },

  {
    id: '07',
    slug: 'friction-on-cycle-wheels',
    title: 'Friction on a cycle',
    topic: 'Friction',
    sim: 'rolling-race',
    setup: 'A cyclist pedals on a level road, and the cycle speeds up.',
    // The cycle, then the claim drawn as said: forward friction at both wheels.
    figA: { box: '40 40 520 282', svg: `
      <g data-t="0.3 0.9">
        <rect x="44" y="272" width="512" height="18" class="ground-hatch"/>
        <line x1="44" y1="272" x2="556" y2="272" class="ln"/>
        <circle cx="140" cy="200" r="72" class="ln" stroke-width="6"/>
        <circle cx="452" cy="200" r="72" class="ln" stroke-width="6"/>
        <g class="ln" stroke-linejoin="round">
          <polyline points="140,200 270,205 245,112 140,200"/>
          <polyline points="245,112 405,118 270,205"/>
          <line x1="405" y1="118" x2="452" y2="200"/>
          <line x1="245" y1="112" x2="240" y2="94"/><line x1="216" y1="92" x2="262" y2="92" stroke-width="7"/>
          <line x1="405" y1="118" x2="398" y2="96"/><line x1="384" y1="96" x2="420" y2="92" stroke-width="6"/>
        </g>
        <g class="ln-mute"><line x1="270" y1="187" x2="140" y2="191"/><line x1="270" y1="223" x2="140" y2="209"/></g>
        <circle cx="270" cy="205" r="18" class="solid"/>
        <circle cx="140" cy="200" r="9" class="solid"/><circle cx="452" cy="200" r="7" class="fill-ink"/>
        <text x="140" y="316" font-size="26" text-anchor="middle" class="t-mute">rear</text>
        <text x="452" y="316" font-size="26" text-anchor="middle" class="t-mute">front</text>
      </g>
      <g data-arrow="250 56 370 56" data-w="6" data-t="1.0 1.5" class="a-green"></g>
      <text x="382" y="68" font-size="38" class="t-v t-green" data-t="1.3 1.6">a</text>
      <g data-arrow="140 272 232 272" data-w="7" data-t="1.7 2.3" class="a-blue"></g>
      <text x="238" y="262" font-size="40" class="t-v t-blue" data-t="2.1 2.4">f</text>
      <g data-arrow="452 272 544 272" data-w="7" data-t="2.2 2.8" class="a-blue"></g>
      <text x="550" y="262" font-size="40" class="t-v t-blue" data-t="2.6 2.9">f</text>` },
    claim: 'Friction is what drives the cycle forward. So friction on both wheels points forward.',
    b: {
      said: 'Friction is what drives the cycle forward. So <s>friction on both wheels points forward</s>.',
      pen: 'Only the rear wheel is pedalled. On the front, friction points back.',
      // Each wheel on its own: what turns it, which way its contact point would
      // slip, and the friction that stops the slip.
      fig: { box: '0 0 600 262', svg: `
        <text x="150" y="26" font-size="24" text-anchor="middle">rear: pedalled</text>
        <rect x="30" y="206" width="240" height="14" class="ground-hatch"/>
        <line x1="30" y1="206" x2="270" y2="206" class="ln"/>
        <circle cx="150" cy="128" r="78" class="ln" stroke-width="5"/>
        <circle cx="150" cy="128" r="7" class="fill-ink"/>
        <path d="M 150 86 A 42 42 0 0 1 192 128" class="ln"/>
        <polygon points="183,118 201,118 192,134" class="fill-ink"/>
        <text x="136" y="104" font-size="22" text-anchor="end" class="t-mute">chain</text>
        <g data-arrow="150 206 240 206" data-w="6" class="a-blue"></g>
        <text x="246" y="196" font-size="30" class="t-v t-blue">f</text>
        <text x="150" y="252" font-size="22" text-anchor="middle" class="t-mute">would slip back: <tspan class="t-blue">f forward</tspan></text>
        <g transform="translate(310 0)">
          <text x="150" y="26" font-size="24" text-anchor="middle">front: pushed</text>
          <rect x="30" y="206" width="240" height="14" class="ground-hatch"/>
          <line x1="30" y1="206" x2="270" y2="206" class="ln"/>
          <circle cx="150" cy="128" r="78" class="ln" stroke-width="5"/>
          <circle cx="150" cy="128" r="7" class="fill-ink"/>
          <g data-arrow="150 128 228 128" data-w="5" class="a-ink"></g>
          <text x="184" y="112" font-size="22" text-anchor="middle" class="t-mute">frame</text>
          <g data-arrow="150 206 60 206" data-w="6" class="a-blue"></g>
          <text x="40" y="196" font-size="30" class="t-v t-blue">f</text>
          <text x="150" y="252" font-size="22" text-anchor="middle" class="t-mute">would slip forward: <tspan class="t-blue">f back</tspan></text>
        </g>` },
      fix: `<p>Rear: friction <b>forward</b>. Front: <b>backward</b>, spinning it faster.</p>
            <p><span class="v">f</span><sub>rear</sub> &gt; <span class="v">f</span><sub>front</sub>, so the cycle speeds up.</p>`,
      keep: 'Friction opposes slipping at the contact point, not motion. Ask which way that point would slide.',
    },
    poll: POLL_07,
    commit: { prompt: POLL_07.question, options: POLL_07.options, answer: POLL_07.answer },
    check: {
      question: 'The cyclist stops pedalling and freewheels on a level road. Which way does friction on the rear wheel point now?',
      options: ['Forward: it still drives the cycle', 'Backward', 'There is none: nothing is pedalling', 'Down, into the road'],
      answer: 1,
      why: 'Nothing turns the rear wheel now. Like the front wheel, it is only pushed along, so friction on it points backward.',
    },
    // With a poll the caption is the poll's question, so it asks; the options
    // are under it, in the poll.
    caption: `What's wrong? #07
A cycle speeds up. Which way does friction on each wheel point?
#physics #friction #whatswrong`,
    adText: 'Friction drives a cycle forward, so it points forward on both wheels? One step is wrong. Spot it, swipe to check, then try it yourself: free, in your browser, no account.',
    // The same ask for a reel ad: no carousel, so nothing to swipe.
    reelAdText: 'Friction drives a cycle forward, so it points forward on both wheels? One step is wrong. Spot it before the answer appears, then try it yourself: free, in your browser, no account.',
  },
];

/** Titles of episodes still to come, in order. Card C lists them after the
 *  published ones, numbered on, so the series reads as longer than this week.
 *  Move a title out of here when its episode is added above. */
export const UPCOMING = ['Rotational motion', 'Moment of inertia', 'Power rating of a bulb', 'Transformer'];

/** The page that lists every episode; the third card of every post points here. */
export const SERIES_URL = 'https://buzzbrain.ai/whatswrong';

/**
 * Where an episode's tap lands: its own page, tagged so the arrival is kept.
 * The student app captures `utm_*` on any landing and files them on the guest
 * if they go on to practise (@shared-ui/acquisition): source, medium, campaign,
 * and `utm_content` as the variant — which episode.
 */
export const landing = (ep, channel) =>
  `${SERIES_URL}/${ep.id}?utm_source=${channel === 'youtube' ? 'youtube' : 'instagram'}&utm_medium=${channel === 'ad' ? 'paid' : 'social'}&utm_campaign=whats-wrong&utm_content=ww${ep.id}`;

/**
 * An episode's YouTube Short: its title, description and tags, from the same
 * caption Instagram gets. The video is the reel rendered for YouTube
 * (render.mjs --format=short): no poll, since a Short cannot carry one.
 *
 * The caption's own ask ("Comment what's wrong, then swipe to check.") is
 * Instagram's — there is nothing to swipe — so it gives way to the Short's.
 * Links in a Short's description are not clickable in the Shorts player, but
 * they are in the description panel, and the tagged one says the arrival came
 * from YouTube.
 */
/** The track under every Short (render.mjs mixes it in), and the credit its
 *  licence asks for, if any, which goes at the foot of each description. */
export const YOUTUBE_MUSIC = null; // { title: '…', artist: '…', credit: 'Music: …' }

export function youtube(ep) {
  const lines = ep.caption.split('\n').slice(1);
  const hashtags = lines.filter((l) => l.startsWith('#')).join(' ').split(/\s+/).filter(Boolean);
  const body = lines.filter((l) => !l.startsWith('#') && !/swipe|^comment/i.test(l));
  const title = `What's wrong? #${ep.id} · ${ep.title} | ${ep.topic}`;
  return {
    title: title.length <= 100 ? title : `What's wrong? #${ep.id} · ${ep.title}`,
    description: [
      ...body,
      // A poll episode's caption asks its question; the Short asks for a comment.
      ep.poll ? 'Comment your answer before it appears.' : "Comment what's wrong before the answer appears.",
      '',
      `Try it yourself, free in your browser: ${landing(ep, 'youtube')}`,
      `Every challenge: ${SERIES_URL}`,
      '',
      [...hashtags, '#shorts'].join(' '),
      ...(YOUTUBE_MUSIC?.credit ? ['', YOUTUBE_MUSIC.credit] : []),
    ].join('\n'),
    tags: [...new Set([...hashtags.map((h) => h.slice(1)), ep.topic.toLowerCase(), 'physics', 'class 11 physics', 'jee physics', 'neet physics', "what's wrong"])],
  };
}
